import { computed, effect, inject, Signal } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivateFn,
    RedirectFunction,
    Route,
    Router,
    UrlSegment,
} from '@angular/router';
import { metaReconnectGuard } from '@core/guards/sync/meta-reconnect.guard';
import { ECOMMERCE_CHANNELS, EcommerceChannels } from '@core/models/admin/ecommerce-account.types';
import { AccountService } from '@core/services/admin/account.service';
import { EcommerceAccountService } from '@core/services/admin/ecommerce-account.service';

import { APP_SLUGS, MAIN_SLUGS } from '@routes/routes.types';
import {
    AllIntegrationChannels,
    EMAIL_CHANNELS,
    EmailChannels,
    GOOGLE_CHANNELS,
    MESSAGING_CHANNELS,
    MessagingChannelsIntegrations,
} from '@shared/types/channels.types';
import { subscriptionGuard } from '../billing/guards/subscription.guard';
import { IntegrationsNavigationService } from './integrations-navigation.service';

import { INTEGRATIONS_SLUGS } from './integrations.constants';
import { SOURCE_CHANNEL } from '@core/models/admin/contacts.types';

/* Pass channel from path to component */

const channelResolver =
    (isParent = false) =>
    (route: ActivatedRouteSnapshot): AllIntegrationChannels => {
        route = isParent ? route : route.parent!;
        const channel = route.paramMap.get('channel');
        return channel?.toUpperCase() as
            | MessagingChannelsIntegrations
            | EcommerceChannels
            | EmailChannels;
    };

/* Redirect to connect page if not connected */

// WhatsappQR integration disabled
// const whatsappWebGuard: CanActivateFn = route => {
//     const accountS = inject(AccountService);
//     const account = computed(() => accountS.account()?.whatsappweb_accounts?.length);
//     return getChannelNotConnectedGuard(account, route);
// };

const ecommerceGuard: CanActivateFn = route => {
    const ecommerceAccountS = inject(EcommerceAccountService);
    return getChannelNotConnectedGuard(ecommerceAccountS.account, route);
};

const googleCalendarsGuard: CanActivateFn = route => {
    const accountS = inject(AccountService);
    const account = computed(() => accountS.account()?.google_calendar_account);
    return getChannelNotConnectedGuard(account, route);
};

const googleAdsGuard: CanActivateFn = route => {
    const accountS = inject(AccountService);
    const adsAccount = computed(() => accountS.account()?.google_adwords_account);
    return getChannelNotConnectedGuard(adsAccount, route);
};

const googleDocsGuard: CanActivateFn = route => {
    const accountS = inject(AccountService);
    const docsAccount = computed(() => accountS.account()?.google_docs_account);
    return getChannelNotConnectedGuard(docsAccount, route);
};

const getChannelNotConnectedGuard = (accountSignal: Signal<any>, route: any) => {
    const navigationS = inject(IntegrationsNavigationService);

    effect(() => {
        if (!accountSignal()) {
            const channel = route.params['channel'];
            if (!channel) throw new Error('No channel in params');
            navigationS.goToChannelConnect(channel);
        }
    });

    return true;
};

/* Redirect to accounts page if connected */

const connectedEcommerceGuard: CanActivateFn = route => {
    const ecommerceAccountS = inject(EcommerceAccountService);
    return getChannelConnectedGuard(ecommerceAccountS.account, route);
};

const getChannelConnectedGuard = (accountSignal: Signal<any>, route: any) => {
    const navigationS = inject(IntegrationsNavigationService);

    effect(() => {
        if (accountSignal()) {
            const channel = route.params['channel'];
            if (!channel) throw new Error('No channel in params');
            navigationS.goToChannelAccounts(channel);
        }
    });
    return true;
};

/* Go to integrations home if incomplete path */

const redirectOnIncompletePath: RedirectFunction = () => {
    const router = inject(Router);
    const segments = [
        APP_SLUGS.MAIN,
        MAIN_SLUGS.integrations,
        MESSAGING_CHANNELS.IG.toLowerCase(),
        INTEGRATIONS_SLUGS.accounts,
    ];
    const tree = router.createUrlTree(segments);
    return tree;
};

/**
 * This is BS
 * Needs refactoring to invert path order
 * /<channel>/account -> /account/<channel>
 * Remember to update whitelisted URLs @ Google and Shopify
 */
export const INTEGRATIONS_ROUTE: Route = {
    title: 'Messaging channels',
    path: '',
    children: [
        {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
                import(
                    '@features/settings/integrations/integrations-overview/integrations-overview.component'
                ).then(m => m.IntegrationsOverviewComponent),
        },
        // Voice numbers management — a static path matched before the `:channel`
        // param so it doesn't collide with the messaging/ecommerce channel matchers.
        {
            path: 'voice',
            title: 'Voice',
            canActivate: [subscriptionGuard],
            loadChildren: () => import('@features/voice/voice.routes').then(m => m.VOICE_ROUTES),
        },
        {
            path: `:channel`,
            loadComponent: () =>
                import(
                    '@features/settings/integrations/select-channel-page/select-channel-page.component'
                ).then(m => m.SelectChannelPageComponent),
            canActivate: [subscriptionGuard],
            resolve: { selectedChannel: channelResolver(true) },
            children: [
                {
                    path: '',
                    pathMatch: 'full',
                    // Angular router wasn't redirecting by just passing a string
                    redirectTo: redirectOnIncompletePath,
                },

                //  Channels with accounts (IG, FB, Whatsapp CLOUD, Whatsapp WEB)
                {
                    title: 'Accounts',
                    matcher: (url, group) => {
                        const match: string[] = [
                            MESSAGING_CHANNELS.IG,
                            MESSAGING_CHANNELS.FB_MESSENGER,
                            MESSAGING_CHANNELS.WA_CLOUD,
                        ];
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (match.includes(channel) && section === INTEGRATIONS_SLUGS.accounts) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    resolve: { selectedChannel: channelResolver() },
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/messaging-channel-accounts/messaging-channel-accounts.component'
                        ).then(m => m.MessagingChannelAccountsComponent),
                },

                // Ecommerce channel account display
                {
                    title: 'Accounts',
                    matcher: (url, group) => {
                        const match: string[] = [
                            ECOMMERCE_CHANNELS.magento,
                            ECOMMERCE_CHANNELS.woocommerce,
                            ECOMMERCE_CHANNELS.shopify,
                        ];
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (match.includes(channel) && section === INTEGRATIONS_SLUGS.accounts) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    canActivate: [ecommerceGuard],
                    resolve: { selectedChannel: channelResolver() },
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/ecommerce-accounts/ecommerce-accounts.component'
                        ).then(m => m.EcommerceAccountsComponent),
                },

                // Connect Meta accounts (IG, FB)
                {
                    title: 'Connect Meta',
                    matcher: (url, group) => {
                        const match: string[] = [
                            MESSAGING_CHANNELS.IG,
                            MESSAGING_CHANNELS.FB_MESSENGER,
                        ];
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (match.includes(channel) && section === INTEGRATIONS_SLUGS.connect) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    canDeactivate: [metaReconnectGuard],
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/connect-meta/connect-meta.component'
                        ).then(m => m.ConnectMetaComponent),
                },

                // Connect WhatsappCloud
                {
                    title: 'Connect Meta',
                    matcher: (url, group) => {
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (
                            channel === MESSAGING_CHANNELS.WA_CLOUD &&
                            section === INTEGRATIONS_SLUGS.connect
                        ) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    canDeactivate: [metaReconnectGuard],
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/connect-meta/connect-whatsapp-cloud/connect-whatsapp-cloud.component'
                        ).then(m => m.ConnectWhatsappCloudComponent),
                },

                // WhatsappQR integration disabled
                // // Whatsapp WEB accounts
                // {
                //     title: 'Whatsapp QR accounts',
                //     matcher: (url, group) => {
                //         const channel = group.segments?.[2]?.toString()?.toUpperCase();
                //         const section = group.segments?.[3]?.toString();

                //         if (
                //             channel === MESSAGING_CHANNELS.WA_WEB &&
                //             section === INTEGRATIONS_SLUGS.accounts
                //         ) {
                //             return {
                //                 consumed: url,
                //                 posParams: { channel: new UrlSegment(channel, {}) },
                //             };
                //         }
                //         return null;
                //     },
                //     canActivate: [whatsappWebGuard],
                //     resolve: { selectedChannel: channelResolver() },
                //     loadComponent: () =>
                //         import(
                //             '@features/settings/integrations/messaging-channel-accounts/messaging-channel-accounts.component'
                //         ).then(m => m.MessagingChannelAccountsComponent),
                // },

                // // Connect Whatsapp WEB
                // {
                //     title: 'Whatsapp QR',
                //     matcher: (url, group) => {
                //         const channel = group.segments?.[2]?.toString()?.toUpperCase();
                //         const section = group.segments?.[3]?.toString();

                //         if (
                //             channel === MESSAGING_CHANNELS.WA_WEB &&
                //             section === INTEGRATIONS_SLUGS.connect
                //         ) {
                //             return {
                //                 consumed: url,
                //                 posParams: { channel: new UrlSegment(channel, {}) },
                //             };
                //         }
                //         return null;
                //     },
                //     loadComponent: () =>
                //         import(
                //             '@features/settings/integrations/whatsapp-qr/whatsapp-qr.component'
                //         ).then(m => m.WhatsappQrComponent),
                // },

                // Connect Shopify
                {
                    title: 'Shopify',
                    matcher: (url, group) => {
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (
                            channel === ECOMMERCE_CHANNELS.shopify &&
                            section === INTEGRATIONS_SLUGS.connect
                        ) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    canActivate: [connectedEcommerceGuard],
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/connect-shopify/connect-shopify.component'
                        ).then(m => m.ConnectShopifyComponent),
                },

                // Connect WooCommerce
                {
                    title: 'WooCommerce',
                    matcher: (url, group) => {
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (
                            channel === ECOMMERCE_CHANNELS.woocommerce &&
                            section === INTEGRATIONS_SLUGS.connect
                        ) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    canActivate: [connectedEcommerceGuard],
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/connect-woocommerce/connect-woocommerce.component'
                        ).then(m => m.ConnectWoocommerceComponent),
                },

                // Connect Magento
                {
                    title: 'Magento',
                    matcher: (url, group) => {
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (
                            channel === ECOMMERCE_CHANNELS.magento &&
                            section === INTEGRATIONS_SLUGS.connect
                        ) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    canActivate: [connectedEcommerceGuard],
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/connect-magento/connect-magento.component'
                        ).then(m => m.ConnectMagentoComponent),
                },

                // Connect Livechat
                {
                    title: 'Livechat',
                    matcher: (url, group) => {
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (
                            channel === MESSAGING_CHANNELS.LIVECHAT &&
                            section === INTEGRATIONS_SLUGS.accounts
                        ) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/livechat-integration/livechat-integration.component'
                        ).then(m => m.LivechatIntegrationComponent),
                },

                // Google Calendar calendars selection
                {
                    title: 'Calendars',
                    matcher: (url, group) => {
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (
                            channel === GOOGLE_CHANNELS.calendar &&
                            section === INTEGRATIONS_SLUGS.accounts
                        ) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    canActivate: [googleCalendarsGuard],
                    resolve: { selectedChannel: channelResolver() },
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/google-calendars/google-calendars.component'
                        ).then(m => m.GoogleCalendarsComponent),
                },

                // Google Ads account
                {
                    title: 'Google Ads',
                    matcher: (url, group) => {
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (
                            channel === GOOGLE_CHANNELS.ads &&
                            section === INTEGRATIONS_SLUGS.accounts
                        ) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    canActivate: [googleAdsGuard],
                    resolve: { selectedChannel: channelResolver() },
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/google-ads-account/google-ads-account.component'
                        ).then(m => m.GoogleAdsAccountComponent),
                },

                // Google Docs account
                {
                    title: 'Google Docs',
                    matcher: (url, group) => {
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (
                            channel === GOOGLE_CHANNELS.docs &&
                            section === INTEGRATIONS_SLUGS.accounts
                        ) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    canActivate: [googleDocsGuard],
                    resolve: { selectedChannel: channelResolver() },
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/google-docs-account/google-docs-account.component'
                        ).then(m => m.GoogleDocsAccountComponent),
                },

                // Connect Google Calendar
                {
                    title: 'Connect Google account',
                    matcher: (url, group) => {
                        const match: string[] = Object.values(GOOGLE_CHANNELS);

                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (match.includes(channel) && section === INTEGRATIONS_SLUGS.connect) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    resolve: { selectedChannel: channelResolver() },
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/connect-google/connect-google.component'
                        ).then(m => m.ConnectGoogleComponent),
                },

                // Select Google Ads account
                {
                    title: 'Select Google Ads account',
                    matcher: (url, group) => {
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (
                            channel === GOOGLE_CHANNELS.ads &&
                            section === INTEGRATIONS_SLUGS.preconnect
                        ) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    resolve: { selectedChannel: channelResolver() },
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/google-ads-select-account/google-ads-select-account.component'
                        ).then(m => m.GoogleAdsSelectAccountComponent),
                },

                // Connect Email (Outlook, Gmail, Generic)
                // Connect Email (Outlook, Gmail, Generic)
                {
                    title: 'Connect Email',
                    matcher: (url, group) => {
                        // Dynamic search for email channel to avoid index issues
                        const segments = group.segments;
                        const channelSegment = segments.find(s =>
                            Object.values(EMAIL_CHANNELS).includes(s.path.toUpperCase() as any)
                        );

                        if (channelSegment) {
                            const channelStr = channelSegment.path.toUpperCase();
                            const index = segments.indexOf(channelSegment);
                            const section = segments[index + 1]?.path;

                            // Match both 'connect' and 'accounts'
                            if (
                                section === INTEGRATIONS_SLUGS.connect ||
                                section === INTEGRATIONS_SLUGS.accounts
                            ) {
                                return {
                                    consumed: url,
                                    posParams: { channel: new UrlSegment(channelStr, {}) },
                                };
                            }
                        }
                        return null;
                    },
                    resolve: { selectedChannel: channelResolver() },
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/connect-email/connect-email.component'
                        ).then(m => m.ConnectEmailComponent),
                },

                // WEB FORMS
                {
                    title: 'Web forms',
                    matcher: (url, group) => {
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (
                            channel === SOURCE_CHANNEL.WEB_FORM &&
                            section === INTEGRATIONS_SLUGS.accounts
                        ) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    resolve: { selectedChannel: channelResolver() },
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/web-forms-list/web-forms-list.component'
                        ).then(m => m.WebFormsListComponent),
                },

                // WEB FORMS
                {
                    title: 'Web forms',
                    matcher: (url, group) => {
                        const channel = group.segments?.[2]?.toString()?.toUpperCase();
                        const section = group.segments?.[3]?.toString();

                        if (
                            channel === SOURCE_CHANNEL.WEB_FORM &&
                            section === INTEGRATIONS_SLUGS.connect
                        ) {
                            return {
                                consumed: url,
                                posParams: { channel: new UrlSegment(channel, {}) },
                            };
                        }
                        return null;
                    },
                    resolve: { selectedChannel: channelResolver() },
                    loadComponent: () =>
                        import(
                            '@features/settings/integrations/web-forms-create/web-forms-create.component'
                        ).then(m => m.WebFormsCreateComponent),
                },
            ],
        },
    ],
};
